import React, { useState, useEffect } from 'react';
import { estimateFare, estimateAllTiers, getFareConfig, getPeakSurcharge } from '../../services/fareService';

export default function FareTestPage() {
  const [rates, setRates] = useState(null);
  const [peakConfig, setPeakConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testResults, setTestResults] = useState([]);

  // Custom test inputs
  const [distanceKm, setDistanceKm] = useState(15);
  const [durationMin, setDurationMin] = useState(22);
  const [tier, setTier] = useState('go');
  const [isPeak, setIsPeak] = useState(false);
  const [peakPercent, setPeakPercent] = useState(25);
  const [customResult, setCustomResult] = useState(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const config = await getFareConfig();
      setRates(config.rates);
      setPeakConfig(config.peak);
      runAutoTests(config.rates, config.peak);
    } catch (err) {
      console.error('Failed to load fare config:', err);
      // Use defaults
      const defaultRates = {
        tiers: {
          go: { baseFare: 8.0, perKm: 1.8, perMin: 0.45 },
          comfy: { baseFare: 12.0, perKm: 2.4, perMin: 0.55 },
          plus: { baseFare: 15.0, perKm: 2.8, perMin: 0.65 },
          vip: { baseFare: 25.0, perKm: 4.0, perMin: 0.85 },
        },
        levy: 1.2,
        gstRate: 0.1,
        minFare: 12.0,
        waitingFreeMinutes: 5,
        waitingFeePerMin: 1.1,
      };
      setRates(defaultRates);
      setPeakConfig({ windows: [], dateOverrides: [] });
      runAutoTests(defaultRates, { windows: [], dateOverrides: [] });
    } finally {
      setLoading(false);
    }
  };

  const runAutoTests = (r, p) => {
    const results = [];

    // F01: Go tier, 15km, 22min, off-peak
    const f01 = estimateFare({ distanceKm: 15, durationMin: 22, tier: 'go', peakMultiplier: 0, tolls: 0, rates: r });
    results.push({
      id: 'F01', name: 'Go tier fare (15km, 22min, off-peak)',
      expected: 'Base $8 + Distance $27 + Duration $9.90 + Levy $1.20 + GST',
      result: f01,
      pass: f01.baseFare === 8 && f01.distanceFare === 27 && f01.durationFare === 9.9,
    });

    // F02: VIP tier, 15km, 22min, off-peak
    const f02 = estimateFare({ distanceKm: 15, durationMin: 22, tier: 'vip', peakMultiplier: 0, tolls: 0, rates: r });
    results.push({
      id: 'F02', name: 'VIP tier fare (15km, 22min, off-peak)',
      expected: 'Base $25 + Distance $60 + Duration $18.70 + Levy $1.20 + GST',
      result: f02,
      pass: f02.baseFare === 25 && f02.distanceFare === 60 && f02.durationFare === 18.7,
    });

    // F03: Peak surcharge (25%)
    const f03 = estimateFare({ distanceKm: 15, durationMin: 22, tier: 'go', peakMultiplier: 0.25, tolls: 0, rates: r });
    results.push({
      id: 'F03', name: 'Go tier with 25% peak surcharge',
      expected: 'Peak surcharge applied, fare increased',
      result: f03,
      pass: f03.peakSurcharge > 0 && f03.totalFare > f01.totalFare,
    });

    // F04: Minimum fare (2km, 5min)
    const f04 = estimateFare({ distanceKm: 2, durationMin: 5, tier: 'go', peakMultiplier: 0, tolls: 0, rates: r });
    results.push({
      id: 'F04', name: 'Minimum fare (2km, 5min)',
      expected: `Fare = $${r.minFare} minimum`,
      result: f04,
      pass: f04.totalFare === r.minFare,
    });

    // F05: All 4 tiers
    const f05 = estimateAllTiers({ distanceKm: 15, durationMin: 22, peakMultiplier: 0, tolls: 0, rates: r });
    results.push({
      id: 'F05', name: 'All 4 tiers display',
      expected: 'Go < Comfy < Plus < VIP',
      result: f05,
      pass: f05.length === 4 &&
        f05[0].totalFare < f05[1].totalFare &&
        f05[1].totalFare < f05[2].totalFare &&
        f05[2].totalFare < f05[3].totalFare,
    });

    // Additional: Comfy tier
    const comfy = estimateFare({ distanceKm: 15, durationMin: 22, tier: 'comfy', peakMultiplier: 0, tolls: 0, rates: r });
    results.push({
      id: 'F05a', name: 'Comfy tier fare (15km, 22min)',
      expected: 'Base $12 + Distance $36 + Duration $12.10 + Levy $1.20 + GST',
      result: comfy,
      pass: comfy.baseFare === 12 && comfy.distanceFare === 36 && comfy.durationFare === 12.1,
    });

    // Additional: Plus tier
    const plus = estimateFare({ distanceKm: 15, durationMin: 22, tier: 'plus', peakMultiplier: 0, tolls: 0, rates: r });
    results.push({
      id: 'F05b', name: 'Plus tier fare (15km, 22min)',
      expected: 'Base $15 + Distance $42 + Duration $14.30 + Levy $1.20 + GST',
      result: plus,
      pass: plus.baseFare === 15 && plus.distanceFare === 42 && plus.durationFare === 14.3,
    });

    // Toll test
    const tollTest = estimateFare({ distanceKm: 15, durationMin: 22, tier: 'go', peakMultiplier: 0, tolls: 6.50, rates: r });
    results.push({
      id: 'F-Toll', name: 'Go tier with $6.50 toll',
      expected: 'Toll added to fare',
      result: tollTest,
      pass: tollTest.tolls === 6.5 && tollTest.totalFare > f01.totalFare,
    });

    setTestResults(results);
  };

  const runCustomTest = () => {
    if (!rates) return;
    const result = estimateFare({
      distanceKm: parseFloat(distanceKm),
      durationMin: parseFloat(durationMin),
      tier,
      peakMultiplier: isPeak ? peakPercent / 100 : 0,
      tolls: 0,
      rates,
    });
    setCustomResult(result);
  };

  if (loading) return <div className="min-h-screen bg-brand-cream px-6 py-8"><p>Loading fare config...</p></div>;

  return (
    <div className="min-h-screen bg-brand-cream px-6 py-8">
      <div className="max-w-[800px] mx-auto">
        <h1 className="font-serif text-[2rem] text-brand-dark mb-2">Fare Engine Test Suite</h1>
        <p className="text-body mb-8">Automated fare calculation verification</p>

        {/* Rate config display */}
        {rates && (
          <div className="bg-white rounded-2xl p-6 border border-slate-200 mb-8">
            <h3 className="font-serif text-[1.2rem] text-brand-dark mb-4">Current Fare Rates</h3>
            <table className="w-full border-collapse mb-2">
              <thead>
                <tr>
                  <th className="text-left px-3 py-2 bg-slate-50 border-b-2 border-slate-200 text-[0.85rem] text-slate-500">Tier</th>
                  <th className="text-left px-3 py-2 bg-slate-50 border-b-2 border-slate-200 text-[0.85rem] text-slate-500">Base</th>
                  <th className="text-left px-3 py-2 bg-slate-50 border-b-2 border-slate-200 text-[0.85rem] text-slate-500">Per Km</th>
                  <th className="text-left px-3 py-2 bg-slate-50 border-b-2 border-slate-200 text-[0.85rem] text-slate-500">Per Min</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(rates.tiers).map(([key, val]) => (
                  <tr key={key}>
                    <td className="px-3 py-2 border-b border-slate-100 text-[0.9rem]"><strong>{key.toUpperCase()}</strong></td>
                    <td className="px-3 py-2 border-b border-slate-100 text-[0.9rem]">${val.baseFare.toFixed(2)}</td>
                    <td className="px-3 py-2 border-b border-slate-100 text-[0.9rem]">${val.perKm.toFixed(2)}</td>
                    <td className="px-3 py-2 border-b border-slate-100 text-[0.9rem]">${val.perMin.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex gap-6 mt-3 text-[0.9rem] text-body">
              <span>Levy: <strong>${rates.levy.toFixed(2)}</strong></span>
              <span>GST: <strong>{rates.gstRate * 100}%</strong></span>
              <span>Min Fare: <strong>${rates.minFare.toFixed(2)}</strong></span>
            </div>
          </div>
        )}

        {/* Auto test results */}
        <h3 className="font-serif text-[1.2rem] text-brand-dark mb-4">Test Results</h3>
        {testResults.map(t => (
          <div key={t.id} className="bg-white rounded-xl p-4 border-2 mb-3" style={{ borderColor: t.pass ? '#2dd4a8' : '#ff6b6b' }}>
            <div className="flex justify-between items-center mb-1.5">
              <span className="font-bold">{t.id}: {t.name}</span>
              <span className="px-2.5 py-[3px] rounded-full text-white text-[0.8rem] font-bold" style={{ background: t.pass ? '#2dd4a8' : '#ff6b6b' }}>
                {t.pass ? '✅ PASS' : '❌ FAIL'}
              </span>
            </div>
            <div className="text-[0.85rem] text-slate-500 mb-1">Expected: {t.expected}</div>
            {t.result && !Array.isArray(t.result) && (
              <div className="text-[0.85rem] text-slate-700 bg-slate-50 p-2.5 rounded-lg mt-1">
                Base ${t.result.baseFare} + Distance ${t.result.distanceFare} + Duration ${t.result.durationFare}
                {t.result.peakSurcharge > 0 && ` + Peak $${t.result.peakSurcharge} (${t.result.peakPercent}%)`}
                {t.result.tolls > 0 && ` + Tolls $${t.result.tolls}`}
                {' '}+ Levy ${t.result.levy} + GST ${t.result.gst}
                {' '}= <strong>${t.result.totalFare}</strong>
              </div>
            )}
            {Array.isArray(t.result) && (
              <div className="text-[0.85rem] text-slate-700 bg-slate-50 p-2.5 rounded-lg mt-1">
                {t.result.map(r => (
                  <div key={r.tier}>{r.tier.toUpperCase()}: <strong>${r.totalFare}</strong></div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Custom calculator */}
        <h3 className="font-serif text-[1.2rem] text-brand-dark mb-4 mt-8">Custom Fare Calculator</h3>
        <div className="bg-white rounded-2xl p-6 border border-slate-200">
          <div className="flex gap-4 mb-4 items-end flex-wrap">
            <div className="flex flex-col gap-1 text-[0.85rem]">
              <label>Distance (km)</label>
              <input type="number" value={distanceKm} onChange={e => setDistanceKm(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-[0.9rem] w-[120px]" />
            </div>
            <div className="flex flex-col gap-1 text-[0.85rem]">
              <label>Duration (min)</label>
              <input type="number" value={durationMin} onChange={e => setDurationMin(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-[0.9rem] w-[120px]" />
            </div>
            <div className="flex flex-col gap-1 text-[0.85rem]">
              <label>Tier</label>
              <select value={tier} onChange={e => setTier(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-[0.9rem] w-[120px]">
                <option value="go">Go</option>
                <option value="comfy">Comfy</option>
                <option value="plus">Plus</option>
                <option value="vip">VIP</option>
              </select>
            </div>
          </div>
          <div className="flex gap-4 mb-4 items-end flex-wrap">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={isPeak} onChange={e => setIsPeak(e.target.checked)} />
              Peak surcharge
            </label>
            {isPeak && (
              <div className="flex flex-col gap-1 text-[0.85rem]">
                <label>Peak %</label>
                <input type="number" value={peakPercent} onChange={e => setPeakPercent(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-[0.9rem] w-[120px]" />
              </div>
            )}
            <button onClick={runCustomTest} className="px-5 py-2 rounded-lg bg-brand-primary text-white border-0 cursor-pointer font-bold text-[0.9rem]">Calculate</button>
          </div>

          {customResult && (
            <div className="mt-4 p-4 bg-slate-50 rounded-xl">
              <div className="flex justify-between py-1 text-[0.9rem]"><span>Base fare</span><span>${customResult.baseFare}</span></div>
              <div className="flex justify-between py-1 text-[0.9rem]"><span>Distance ({customResult.distanceKm}km × ${rates.tiers[tier].perKm}/km)</span><span>${customResult.distanceFare}</span></div>
              <div className="flex justify-between py-1 text-[0.9rem]"><span>Duration ({customResult.durationMin}min × ${rates.tiers[tier].perMin}/min)</span><span>${customResult.durationFare}</span></div>
              {customResult.peakSurcharge > 0 && <div className="flex justify-between py-1 text-[0.9rem]"><span>Peak surcharge ({customResult.peakPercent}%)</span><span>${customResult.peakSurcharge}</span></div>}
              <div className="flex justify-between py-1 text-[0.9rem]"><span>NSW P2P Levy</span><span>${customResult.levy}</span></div>
              <div className="flex justify-between py-1 text-[0.9rem]"><span>GST (10%)</span><span>${customResult.gst}</span></div>
              <div className="flex justify-between py-1 text-[0.9rem] font-bold text-[1.1rem] border-t-2 border-slate-200 pt-2 mt-1">
                <span>Total Fare</span><span>${customResult.totalFare}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}